import * as React from "react";
import { Label, LabelConfig, SingleValueComponent } from "ui/common";
import { ReactNode } from "react";
import { routeConstants } from "app/routes/route-constants";
import * as navigate from "app/area/shared/navigation";
import { Link, useNavigate, useLocation } from "react-router-dom";
import styles from "./display-link.scss";
import { extractCaseIdFromUrl, isCaseViewModalOpen, normalizeUrl, shouldNavigateToModal } from "app/components/helpers";
import { isNumeric } from "ui/display-number/display-number";
import { AuthState } from "app/types-shared";
import { useSharedState } from "app/infrastructure/redux/hooks";

export interface DisplayLinkProps extends SingleValueComponent<number> {
  withPrefix?: boolean;
  url?: string;
  forceStandalone?: boolean;
  customText?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  navigateToUrlForNewTab ?: boolean;
  section?: number;
  urlOpenNew?: boolean;
  deepLink?: boolean;
  /**
   * @internal
   */
  params?: { favouriteId?: number; dashboardType?: number; bowtieCaseId?: number };
  children?: ReactNode;
  label?: LabelConfig;
  value?: number;
  urlHrefNewTabOverride?: string;
}

export const DisplayLink = (props: Partial<DisplayLinkProps>) => {
  const { className, componentId, label, url = "", withPrefix = false, children,
    forceStandalone = false,  params, customText, value, navigateToUrlForNewTab = false, 
    section, urlOpenNew, deepLink, urlHrefNewTabOverride } = props;

  const navigateOnClick = useNavigate();
  const location = useLocation();
  const sharedState = useSharedState();
  
  const extractedCaseIdFromUrl = url && extractCaseIdFromUrl(url);
  const caseIdFromUrl = extractedCaseIdFromUrl ? Number(extractedCaseIdFromUrl) : undefined;
  const resolvedCaseId = value ?? caseIdFromUrl ?? null;
  const resolvedUrlHrefNewTab = resolvedCaseId ? (section ? routeConstants.caseSectionFor(resolvedCaseId, section) : url) : undefined;

  if ((!resolvedCaseId || !isNumeric(resolvedCaseId)) && !children) {
    return null;
  }

  const resolvedText = customText ?? resolvedCaseId;
  const resolvedPrefixedText = withPrefix ? `#${resolvedText}` : resolvedText;
  const caseViewUrl = sharedState.authState === AuthState.Anonymous ? 
    routeConstants.caseEditFor(resolvedCaseId) : routeConstants.caseViewFor(resolvedCaseId);
  
  const childEntityPath = deepLink ? '/' + url?.split('/case/')?.[1]?.split('/')?.slice(1)?.join('/') : '';
  const caseUrl = resolvedCaseId ? 
    (section ? routeConstants.caseViewFor(resolvedCaseId) + childEntityPath + routeConstants.caseSectionFor(section, false) :
      caseViewUrl + childEntityPath) : undefined;
  const hrefForNewTab = resolvedCaseId ? caseUrl : "#";

  const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return; // let the browser handle middle/modified clicks
    }
    if (urlOpenNew) {
      // let the browser handle opening a new tab
      return;
    }
    e.preventDefault();

    if (props.onClick) {
      props.onClick(e);
      return;
    }

    if (url) {
      // direct navigation using react-router
      navigateOnClick(normalizeUrl(url), { replace: true });
      return;
    }

    // app-specific navigation helpers
    if(resolvedCaseId !== null) {
      if (shouldNavigateToModal(!!params?.favouriteId, !!params?.dashboardType, !!params?.bowtieCaseId)) {
        if (forceStandalone && isCaseViewModalOpen()) {
          navigate.toCaseView(resolvedCaseId); // case header link in case header
        } else {
          navigate.toCaseViewModal(resolvedCaseId);
        }
      } else {
        sharedState.authState === AuthState.Anonymous ? 
        navigate.toCaseEdit(resolvedCaseId) : navigate.toCaseView(resolvedCaseId);
      }
    }
  };

  const resolvedOnClick = props.onClick ?? handleOnClick;
  const resolvedHrefForNewTab = navigateToUrlForNewTab ? (urlHrefNewTabOverride || resolvedUrlHrefNewTab || hrefForNewTab) : hrefForNewTab;

  return (
    <Link id={componentId} className={`${styles.displayLink} ${className}`} 
      state={{ previousLocation: location }} to={resolvedHrefForNewTab} 
      onClick={resolvedOnClick} target={urlOpenNew && '_blank'} replace={true}  >
      {children ? (
        <>{children}</>
      ) : (
        <>
          <Label label={label} />
          {resolvedPrefixedText}
        </>
      )}
    </Link>
  );
};
